import { useEffect, useState } from "react";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { ensureCustomerProfile } from "@/lib/customer/customer-api";
import { requestWelcomeEmail } from "@/lib/customerio/customerio-welcome.functions";

/**
 * Pathless layout guarding the customer account surface.
 *
 * ssr:false because the Supabase session lives in localStorage. This is a
 * client-side UX guard only — the real boundary is RLS on customer_profiles,
 * customer_addresses and customer_audit_log, which scope every row to
 * current_customer_id().
 */
export const Route = createFileRoute("/_customer")({
  ssr: false,
  component: CustomerGate,
});

function CustomerGate() {
  const navigate = useNavigate();
  const search = Route.useSearch() as { redirect?: string };
  const [state, setState] = useState<"checking" | "ready" | "redirecting">("checking");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.auth.getUser();
      if (cancelled) return;
      
      if (error || !data.user) {
        setState("redirecting");
        const currentPath = window.location.pathname + window.location.search;
        navigate({ 
          to: "/account/sign-in", 
          search: { redirect: currentPath },
          replace: true 
        });
        return;
      }

      // Ensure profile exists. It returns the profile UUID.
      try {
        await ensureCustomerProfile();
      } catch (e) {
        console.error("Failed to initialize customer profile:", e);
      }
      // Welcome email: fire-and-forget; server decides eligibility (OFF by default).
      requestWelcomeEmail().catch(() => {});
      
      if (cancelled) return;
      setState("ready");
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        setState("redirecting");
        navigate({ to: "/account/sign-in", replace: true });
      } else if (event === "SIGNED_IN" && session) {
        // Handle case where session is restored after a glitch
        setState("ready");
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [navigate]);

  if (state !== "ready") {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <p className="text-sm text-muted-foreground">Loading your account…</p>
      </div>
    );
  }

  return <Outlet />;
}
